import { type RefObject, useEffect } from "react";

/**
 * Keyboard activation for a `Pressable` that react-native-web renders as a roled `<div>` (#785).
 *
 * RNW activates such an element on Enter but not Space, because its keyboard press handler treats
 * Space as a button-only key and none of these are `role="button"`. Space is the activation key for
 * `role="checkbox"` and `role="radio"` (WCAG 2.1.1 and the ARIA checkbox and radio patterns), so
 * without this, Space does nothing on those controls for a web keyboard user.
 *
 * It calls `stopPropagation` as well as `preventDefault` because a container may also listen for
 * Space (the desktop inventory grid toggles its cursor row), and the focused control has already
 * handled the key. `preventDefault` also stops the page from scrolling.
 *
 * Returned as an opaque `object` so it can be spread onto a `Pressable`: `onKeyDown` is a DOM prop
 * that RNW forwards but React Native's types do not declare. On native the prop has no effect.
 */
export const spaceActivates = (press: () => void) =>
  ({
    onKeyDown: (
      e: {
        key?: string;
        metaKey?: boolean;
        ctrlKey?: boolean;
        altKey?: boolean;
        preventDefault?: () => void;
        stopPropagation?: () => void;
      },
    ) => {
      if (e.key !== " " && e.key !== "Spacebar") return;
      // Like `tableKeyIntent`, leave modifier chords to the browser and the OS, so Cmd, Ctrl or
      // Alt+Space keeps working while a control inside the page has focus.
      if (e.metaKey === true || e.ctrlKey === true || e.altKey === true) return;
      e.preventDefault?.();
      e.stopPropagation?.();
      press();
    },
  }) as object;

/**
 * Closes a web dialog on an Escape nothing else handled.
 *
 * react-native-web's `TextInput` calls `stopPropagation` on every keydown (TRO-2076), so an Escape
 * typed into an RN field never reaches a React Aria dialog's own handler, and the dialog stays
 * open. A native listener on the dialog element sees the key before RNW stops it; waiting one task
 * lets any real handler (the dialog itself, or a field closing its own popup) mark it handled with
 * `preventDefault` first, so the dialog never closes twice or over an open popup.
 */
export function useCloseOnUnhandledEscape(
  ref: RefObject<HTMLElement | null>,
  close: () => void,
  active: boolean,
) {
  useEffect(() => {
    const element = ref.current;
    if (!active || !element) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.isComposing) return;
      setTimeout(() => {
        if (!event.defaultPrevented) close();
      }, 0);
    };
    element.addEventListener("keydown", onKeyDown);
    return () => element.removeEventListener("keydown", onKeyDown);
  }, [ref, close, active]);
}
